import { XMLBuilder, XMLParser } from 'fast-xml-parser'

import { useNotifications } from '@/composables/useNotifications'
import HttpFactory from '@/drivers/http'
import { t } from '@/i18n'

import type { CashMachineOptions, ICashMachine } from './types'

export class Glory implements ICashMachine {
  private initialized: boolean = false
  private http!: any
  private config!: CashMachineOptions
  private inputAmountListener!: (amount: number) => void

  private cancelPromise!: Promise<number>
  private cancelPromiseResolve!: (value: number) => void

  private xmlParser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    textNodeName: '#text'
  })

  private xmlBuilder = new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    format: true,
    indentBy: '  '
  })

  constructor() {}

  async init(config: CashMachineOptions) {
    this.http = HttpFactory.getHttp()
    this.config = config
    this.inputAmountListener = config.inputAmountListener
    this.initialized = true
  }

  async isAvailable() {
    const xmlObject = {
      'soap:Envelope': {
        '@_xmlns:soap': 'http://schemas.xmlsoap.org/soap/envelope/',
        'soap:Body': {
          StatusRequest: {
            '@_xmlns': 'http://www.glory.co.jp/bruebox.xsd',
            Id: 0,
            SeqNo: 1,
            Option: {
              '@_type': 0
            }
          }
        }
      }
    }

    const data = this.xmlBuilder.build(xmlObject)

    const response = (
      await this.http.post(
        `http://${this.config.ip}/axis2/services/BrueBoxService`,
        {
          'Content-Type': 'text/xml; charset=utf-8',
          SOAPAction: 'GetStatus'
        },
        data
      )
    ).data

    const result = this.xmlParser.parse(response)
    const statusResponse = result['soapenv:Envelope']['soapenv:Body']['n:StatusResponse']
    return statusResponse['Status']['n:Code'] == '1'
  }

  async status() {
    const xmlObject = {
      'soap:Envelope': {
        '@_xmlns:soap': 'http://schemas.xmlsoap.org/soap/envelope/',
        'soap:Body': {
          StatusRequest: {
            '@_xmlns': 'http://www.glory.co.jp/bruebox.xsd',
            Id: 0,
            SeqNo: 1,
            Option: {
              '@_type': 1
            }
          }
        }
      }
    }

    const data = this.xmlBuilder.build(xmlObject)

    const response = (
      await this.http.post(
        `http://${this.config.ip}/axis2/services/BrueBoxService`,
        {
          'Content-Type': 'text/xml; charset=utf-8',
          SOAPAction: 'GetStatus'
        },
        data
      )
    ).data

    const result = this.xmlParser.parse(response)
    const statusResponse = result['soapenv:Envelope']['soapenv:Body']['n:StatusResponse']

    const amount = this.getAmountPayed(statusResponse)
    // @ts-expect-error We are not sure about the type here
    if (isNaN(parseInt(amount))) this.inputAmountListener(0)
    // @ts-expect-error We are not sure about the type here
    else this.inputAmountListener(parseInt(amount))
  }

  async charge(amount: number) {
    const { notifyError } = useNotifications()
    if (this.initialized) {
      const available = await this.isAvailable()
      if (!available) {
        notifyError({
          title: t('notifications.temporarily-unavailable')
        })
        return 0
      }
      const xmlObject = {
        'soap:Envelope': {
          '@_xmlns:soap': 'http://schemas.xmlsoap.org/soap/envelope/',
          'soap:Body': {
            ChangeRequest: {
              '@_xmlns': 'http://www.glory.co.jp/bruebox.xsd',
              Id: 0,
              SeqNo: 1,
              Amount: amount,
              Option: {
                '@_type': 0
              }
            }
          }
        }
      }

      const data = this.xmlBuilder.build(xmlObject)

      const resolveCharge = async () => {
        const response = (
          await this.http.post(
            `http://${this.config.ip}/axis2/services/BrueBoxService`,
            {
              'Content-Type': 'text/xml; charset=utf-8',
              SOAPAction: 'ChangeOperation'
            },
            data
          )
        ).data
        const result = this.xmlParser.parse(response)
        const changeResponse = result['soapenv:Envelope']['soapenv:Body']['n:ChangeResponse']
        const status = changeResponse['@_n:result']
        if (status == '0') {
          return amount
        } else if (status == '10') {
          notifyError({
            title: t('notifications.payment-failed')
          })
          const payed = this.getAmountPayed(changeResponse)
          let isAvailable
          while (!isAvailable) {
            isAvailable = await this.isAvailable()
          }
          // @ts-expect-error We are not sure about the type here
          await this.charge(-parseInt(payed))
          return 0
        } else {
          return 0
        }
      }

      const statusInterval = setInterval(() => this.status(), 1000)
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this
      this.cancelPromise = new Promise((resolve, _reject) => {
        that.cancelPromiseResolve = resolve
      })
      const charged = await Promise.race([resolveCharge(), this.cancelPromise])
      clearInterval(statusInterval)
      return charged
    } else {
      return 0
    }
  }

  async payIn(amount: number) {
    return this.charge(amount)
  }

  async payOut(amount: number) {
    return -(await this.charge(-amount))
  }

  async cancel() {
    const xmlObject = {
      'soap:Envelope': {
        '@_xmlns:soap': 'http://schemas.xmlsoap.org/soap/envelope/',
        'soap:Body': {
          ChangeCancelRequest: {
            '@_xmlns': 'http://www.glory.co.jp/bruebox.xsd',
            Id: 0,
            SeqNo: 1,
            Option: {
              '@_type': 0
            }
          }
        }
      }
    }

    const data = this.xmlBuilder.build(xmlObject)

    await this.http.post(
      `http://${this.config.ip}/axis2/services/BrueBoxService`,
      {
        'Content-Type': 'text/xml; charset=utf-8',
        SOAPAction: 'ChangeCancelOperation'
      },
      data
    )

    if (this.cancelPromiseResolve) {
      this.cancelPromiseResolve(0)
    }
  }

  private getAmountPayed(response: any) {
    let payed = 0

    if (!response?.Cash) {
      return 0
    }

    const cashArray = Array.isArray(response.Cash) ? response.Cash : [response.Cash]

    cashArray.forEach((cash: any) => {
      if (!cash?.Denomination) return

      const denominations = Array.isArray(cash.Denomination)
        ? cash.Denomination
        : [cash.Denomination]

      payed += denominations.reduce((total: number, denomination: any) => {
        const faceValue = parseFloat(denomination['@_n:fv'] || '0')
        const pieces = parseFloat(denomination['n:Piece'] || '0')
        return total + faceValue * pieces
      }, 0)
    })

    return payed
  }
}

export default new Glory()
